import {EmblaCarouselType} from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import React, {Fragment, useCallback, useEffect, useState} from 'react';
import {flushSync} from 'react-dom';

import {Typography} from '@mui/material';
import {CarrouselTypeEnum} from 'presentation/constants/carrouselTypeEnum';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {NextButton, PrevButton} from './ArrowsAndDotsButtons';
import {
	ArrowsAnchor,
	ArrowsContainer,
	DotsContainer,
	StyledDotButton,
} from './ArrowsAndDotsButtons/styles';
import {useCarrouselIndexStore} from './store';
import {InnerContainer, OuterContainer, Slide, StyledButton} from './styles';
import {CarrouselProps} from './types';

const {next, prev} = translations['pt-br'].commons;

const TWEEN_FACTOR = 1.2;

const numberWithinRange = (number: number, min: number, max: number): number =>
	Math.min(Math.max(number, min), max);

function Carousel({
	numbersText,
	slides,
	isFullscreen,
	options,
	hideButtons,
	hideDots,
	isDarkTheme,
	hasOpacityEffect,
	controlledSelectedIndex,
	setControlledSelectedIndex,
	type = CarrouselTypeEnum.ARROWS,
	plugins,
	controlledIndex,
	prevDataTagId,
	textDataTagId,
	nextDataTagId,
}: CarrouselProps) {
	const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);
	const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
	const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
	const setCarrouselSlideIndex = useCarrouselIndexStore(state => state.setCarrouselIndex);

	const isNumbersType = type === CarrouselTypeEnum.NUMBERS;
	const isDotsType = type === CarrouselTypeEnum.DOTS;
	const isArrowsType = type === CarrouselTypeEnum.ARROWS;
	const isArrowsDotsType = type === CarrouselTypeEnum.ARROWS_DOTS;

	const scrollPrev = useCallback(() => {
		if (emblaApi) {
			emblaApi.scrollPrev();
			emblaApi.plugins()?.autoplay?.reset();
		}
	}, [emblaApi]);
	const scrollNext = useCallback(() => {
		if (emblaApi) {
			emblaApi.scrollNext();
			emblaApi.plugins()?.autoplay?.reset();
		}
	}, [emblaApi]);
	const scrollTo = useCallback(
		(index: number) => emblaApi && emblaApi.scrollTo(index),
		[emblaApi],
	);

	const onInit = useCallback((api: EmblaCarouselType) => {
		setScrollSnaps(api.scrollSnapList());
	}, []);

	const onSelect = useCallback((api: EmblaCarouselType) => {
		setSelectedIndex(api.selectedScrollSnap());

		if (setControlledSelectedIndex) {
			setControlledSelectedIndex(api.selectedScrollSnap());
		}

		setPrevBtnDisabled(!api.canScrollPrev());
		setNextBtnDisabled(!api.canScrollNext());
	}, []);

	const [tweenValues, setTweenValues] = useState<number[]>([]);
	const onScroll = useCallback(() => {
		if (!emblaApi) return;

		const engine = emblaApi.internalEngine();
		const scrollProgress = emblaApi.scrollProgress();

		const styles = emblaApi.scrollSnapList().map((scrollSnap, index) => {
			let diffToTarget = scrollSnap - scrollProgress;
			if (engine.options.loop) {
				engine.slideLooper.loopPoints.forEach(loopItem => {
					const target = loopItem.target();
					if (index === loopItem.index && target !== 0) {
						const sign = Math.sign(target);
						if (sign === -1) diffToTarget = scrollSnap - (1 + scrollProgress);
						if (sign === 1) diffToTarget = scrollSnap + (1 - scrollProgress);
					}
				});
			}
			const tweenValue = 1 - Math.abs(diffToTarget * TWEEN_FACTOR);
			return numberWithinRange(tweenValue, 0, 1);
		});
		setTweenValues(styles);
	}, [emblaApi, setTweenValues]);

	useEffect(() => {
		if (!emblaApi) return;

		onInit(emblaApi);
		onSelect(emblaApi);
		emblaApi.on('reInit', onInit);
		emblaApi.on('reInit', onSelect);
		emblaApi.on('select', onSelect);
		onScroll();
		emblaApi.on('scroll', () => {
			flushSync(() => onScroll());
		});
		emblaApi.on('reInit', onScroll);

		// eslint-disable-next-line consistent-return
		return () => {
			emblaApi.off('reInit', onInit);
			emblaApi.off('reInit', onSelect);
			emblaApi.off('select', onSelect);
			onScroll();
			emblaApi.off('scroll', () => {
				flushSync(() => onScroll());
			});
			emblaApi.off('reInit', onScroll);
		};
	}, [emblaApi, onInit, onSelect]);

	useEffect(() => {
		if (!emblaApi) return;
		if (controlledIndex) {
			setCarrouselSlideIndex(selectedIndex);
		}
	}, [emblaApi, controlledIndex, selectedIndex]);

	return (
		<Fragment>
			{isNumbersType ? (
				<Fragment>
					{scrollSnaps.length < 2 ? null : (
						<DotsContainer
							id="dots-container"
							className="dots-container"
							$hideButtons={hideButtons}>
							<StyledButton
								$isDarkTheme={isDarkTheme}
								onClick={scrollPrev}
								disabled={prevBtnDisabled}
								sx={{marginRight: 'auto', lineHeight: 0}}
								variant="text"
								size="small"
								startIcon={<Icon name="arrow-left-filled" size={18} />}
								data-tag-id={prevDataTagId}>
								{prev}
							</StyledButton>

							{numbersText ? (
								<Typography variant="body2" data-tag-id={textDataTagId}>
									<b>{selectedIndex + 1}</b> de <b>{scrollSnaps.length}</b>{' '}
									{numbersText}
								</Typography>
							) : (
								<Typography variant="body2" data-tag-id={textDataTagId}>
									{selectedIndex + 1}/{scrollSnaps.length}
								</Typography>
							)}

							<StyledButton
								$isDarkTheme={isDarkTheme}
								disabled={nextBtnDisabled}
								onClick={scrollNext}
								sx={{marginLeft: 'auto', lineHeight: 0}}
								variant="text"
								size="small"
								endIcon={<Icon name="arrow-right-filled" size={18} />}
								data-tag-id={nextDataTagId}>
								{next}
							</StyledButton>
						</DotsContainer>
					)}
				</Fragment>
			) : null}
			<OuterContainer
				id="outer-container"
				$type={type}
				$hasRightEdgeFade={emblaApi?.canScrollNext()}
				$hasLeftEdgeFade={emblaApi?.canScrollPrev()}>
				<div id="inner-container" ref={emblaRef}>
					<InnerContainer id="slide-container" $type={type}>
						{slides.map((slide, index) => (
							<Slide
								onClick={() => scrollTo(index)}
								key={index}
								className="slide"
								$isFullscreen={isFullscreen}
								style={
									hasOpacityEffect
										? {
												...(tweenValues.length && {
													opacity: tweenValues[index],
												}),
											}
										: undefined
								}>
								{slide}
							</Slide>
						))}
					</InnerContainer>
				</div>

				{isArrowsType || isArrowsDotsType ? (
					<ArrowsContainer>
						<ArrowsAnchor>
							<PrevButton onClick={scrollPrev} disabled={prevBtnDisabled} />
							<NextButton onClick={scrollNext} disabled={nextBtnDisabled} />
						</ArrowsAnchor>
					</ArrowsContainer>
				) : null}
			</OuterContainer>

			{isDotsType || isArrowsDotsType ? (
				<Fragment>
					{scrollSnaps.length < 2 ? null : (
						<DotsContainer
							className="dots-container"
							$hideButtons={hideButtons || isArrowsDotsType}>
							{hideButtons || isArrowsDotsType ? null : (
								<StyledButton
									$isDarkTheme={isDarkTheme}
									onClick={scrollPrev}
									disabled={prevBtnDisabled}
									sx={{marginRight: 'auto', lineHeight: 0}}
									variant="text"
									size="small"
									startIcon={<Icon name="arrow-left-filled" size={18} />}>
									{prev}
								</StyledButton>
							)}
							{hideDots
								? null
								: scrollSnaps.map((_, index) => (
										<StyledDotButton
											key={index}
											onClick={() => scrollTo(index)}
											$isActive={
												controlledSelectedIndex
													? index === controlledSelectedIndex
													: index === selectedIndex
											}
											$isDarkTheme={isDarkTheme}
										/>
									))}
							{hideButtons || isArrowsDotsType ? null : (
								<StyledButton
									$isDarkTheme={isDarkTheme}
									disabled={nextBtnDisabled}
									onClick={scrollNext}
									sx={{marginLeft: 'auto', lineHeight: 0}}
									variant="text"
									size="small"
									endIcon={<Icon name="arrow-right-filled" size={18} />}>
									{next}
								</StyledButton>
							)}
						</DotsContainer>
					)}
				</Fragment>
			) : null}
		</Fragment>
	);
}

export {Carousel};
